/**
 * ModelComparison - Compare LLM models on the same workflow step
 * Runs the same prompt against multiple models in parallel and shows results side-by-side.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  Loader2,
  Clock,
  DollarSign,
  CheckCircle2,
  XCircle,
  Copy,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { AVAILABLE_MODELS, DEFAULT_COMPARISON_MODELS } from './modelComparisonModels';

export { AVAILABLE_MODELS, DEFAULT_COMPARISON_MODELS };

interface ModelComparisonProps {
  /** The step configuration to compare across models */
  step: {
    name: string;
    prompt: string;
    provider?: string;
    model?: string;
    max_tokens?: number;
    temperature?: number;
    input?: any;
  };
  /** Test input to use */
  input?: any;
  /** Callback when comparison completes */
  onComplete?: (results: ComparisonResult[]) => void;
}

interface ComparisonResult {
  model: string;
  provider: string;
  success: boolean;
  result?: any;
  error?: string;
  duration_ms?: number | null;
  tokens_used?: number | null;
  cost_usd?: number | null;
}

export default function ModelComparison({ step, input, onComplete }: ModelComparisonProps) {
  const [selectedModels, setSelectedModels] = useState<string[]>([...DEFAULT_COMPARISON_MODELS]);
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [running, setRunning] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  const toggleModel = (modelId: string) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(m => m !== modelId)
        : [...prev, modelId]
    );
  };

  const runComparison = async () => {
    if (selectedModels.length < 2) {
      toast.error('Select at least 2 models to compare');
      return;
    }

    setRunning(true);
    setResults([]);
    setExpandedResults(new Set());

    try {
      const tenantId = localStorage.getItem('tenant_id') || 'default-tenant';
      const response = await fetch('/cp/api/volcano/compare', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId,
        },
        body: JSON.stringify({
          step: {
            name: step.name,
            prompt: step.prompt,
            max_tokens: step.max_tokens || 2000,
            temperature: step.temperature,
          },
          models: selectedModels,
          input: input || {},
        }),
      });

      const data = await response.json();
      if (data.success && data.comparisons) {
        setResults(data.comparisons);
        setExpandedResults(new Set(data.comparisons.map((_: any, i: number) => i)));
        onComplete?.(data.comparisons);
        toast.success(`Compared ${data.comparisons.length} models`);
      } else {
        throw new Error(data.error || 'Comparison failed');
      }
    } catch (err: any) {
      toast.error(`Comparison failed: ${err.message}`);
    } finally {
      setRunning(false);
    }
  };

  const copyResult = (result: any) => {
    navigator.clipboard.writeText(typeof result === 'string' ? result : JSON.stringify(result, null, 2));
    toast.success('Copied to clipboard');
  };

  // Find fastest/cheapest for highlighting
  const successResults = results.filter(r => r.success);
  const fastest = successResults.length > 0
    ? successResults.reduce((a, b) => ((a.duration_ms || Infinity) < (b.duration_ms || Infinity) ? a : b))
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-sm">Model Comparison</h3>
        <Badge variant="outline" className="text-[10px]">{step.name}</Badge>
      </div>

      {/* Model Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-gray-600">Select Models (min 2):</label>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_MODELS.map(model => (
            <button
              key={model.id}
              onClick={() => toggleModel(model.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                selectedModels.includes(model.id)
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {model.label}
            </button>
          ))}
        </div>
      </div>

      {/* Run Button */}
      <Button
        size="sm"
        onClick={runComparison}
        disabled={running || selectedModels.length < 2}
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
      >
        {running ? (
          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Comparing...</>
        ) : (
          <><Play className="w-4 h-4 mr-1" /> Compare {selectedModels.length} Models</>
        )}
      </Button>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          {results.map((r, i) => (
            <div
              key={i}
              className={`border rounded-lg overflow-hidden ${
                r.success ? 'border-gray-200' : 'border-red-200'
              } ${fastest && r.model === fastest.model ? 'ring-2 ring-green-300' : ''}`}
            >
              {/* Result Header */}
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                <div className="flex items-center gap-2">
                  {r.success ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-xs font-semibold">{r.model}</span>
                  <Badge variant="secondary" className="text-[10px]">{r.provider}</Badge>
                  {fastest && r.model === fastest.model && (
                    <Badge className="text-[10px] bg-green-100 text-green-700 border-green-300">Fastest</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-500">
                  {r.duration_ms != null && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      {r.duration_ms < 1000 ? `${r.duration_ms}ms` : `${(r.duration_ms / 1000).toFixed(1)}s`}
                    </span>
                  )}
                  {r.tokens_used != null && <span>{r.tokens_used} tokens</span>}
                  {r.cost_usd != null && (
                    <span className="flex items-center gap-0.5">
                      <DollarSign className="w-3 h-3" />${r.cost_usd.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>

              {/* Result Content */}
              <div className="p-3">
                {r.error && (
                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{r.error}</div>
                )}
                {r.result != null && (
                  <div className="relative">
                    <button
                      className="absolute top-1 right-1 text-[10px] text-blue-500 hover:underline flex items-center gap-0.5 z-10"
                      onClick={() => copyResult(r.result)}
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <pre className="bg-gray-900 text-green-400 p-2 rounded text-[10px] font-mono overflow-x-auto max-h-32 overflow-y-auto">
                      {typeof r.result === 'string'
                        ? r.result.substring(0, 1000)
                        : JSON.stringify(r.result, null, 2).substring(0, 1000)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
